import { useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  RFID_EXPORT_COLUMNS,
  RFID_HISTORY_COLUMNS,
  getExportDateTime,
  getHistoryForRow,
  loadAllRfidHistory,
  mapHistoryRowsForExport,
  mapRfidRowsForExport,
} from "./rfidExportHelpers";
import { useRfidStore } from "./store/useRfidStore";

const MAIN_HEAD_STYLES = {
  fillColor: [0, 51, 102],
  textColor: [255, 255, 255],
  fontSize: 7,
  halign: "center",
};

const MAIN_BODY_STYLES = {
  fontSize: 6,
  halign: "center",
};

const HISTORY_HEAD_STYLES = {
  fillColor: [219, 234, 254],
  textColor: [0, 51, 102],
  fontSize: 7,
  halign: "center",
};

const HISTORY_BODY_STYLES = {
  fontSize: 6,
  halign: "center",
};

const getStartY = (doc) => doc.lastAutoTable?.finalY ?? 35;

const addPageIfNeeded = (doc, startY, minSpace = 30) => {
  const pageHeight = doc.internal.pageSize.height;
  if (startY + minSpace > pageHeight - 14) {
    doc.addPage();
    return 20;
  }
  return startY;
};

export const ExportRfidPdf = ({ rfidData = [] }) => {
  const fetchRfidHistory = useRfidStore((state) => state.fetchRfidHistory);
  const [isExporting, setIsExporting] = useState(false);

  const exportToPDF = async () => {
    setIsExporting(true);

    try {
      const historyById = await loadAllRfidHistory(rfidData, fetchRfidHistory);
      const mainRows = mapRfidRowsForExport(rfidData);
      const doc = new jsPDF({ orientation: "landscape" });
      const pageWidth = doc.internal.pageSize.width;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text("RFID List", pageWidth / 2, 20, { align: "center" });
      doc.setFontSize(10);
      doc.text(getExportDateTime(), pageWidth / 2, 28, { align: "center" });

      let startY = 35;

      mainRows.forEach((mainRow, index) => {
        const device = rfidData[index];
        const history = getHistoryForRow(device, historyById);

        startY = addPageIfNeeded(doc, startY, 12);

        autoTable(doc, {
          startY,
          head: index === 0 ? [RFID_EXPORT_COLUMNS] : [],
          body: [mainRow],
          headStyles: MAIN_HEAD_STYLES,
          bodyStyles: MAIN_BODY_STYLES,
          margin: { left: 14, right: 14 },
        });

        startY = getStartY(doc) + 4;

        if (!history.length) return;

        startY = addPageIfNeeded(doc, startY, 24);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setFillColor(219, 234, 254);
        doc.rect(14, startY - 5, pageWidth - 28, 8, "F");
        doc.setTextColor(0, 51, 102);
        doc.text(`History - ${device.rfidCode || "N/A"}`, 18, startY);
        doc.setTextColor(0, 0, 0);

        autoTable(doc, {
          startY: startY + 3,
          head: [RFID_HISTORY_COLUMNS],
          body: mapHistoryRowsForExport(history),
          headStyles: HISTORY_HEAD_STYLES,
          bodyStyles: HISTORY_BODY_STYLES,
          margin: { left: 22, right: 14 },
        });

        startY = getStartY(doc) + 8;
      });

      doc.save("RfidList.pdf");
    } catch (error) {
      console.error("Error generating PDF file:", error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <button
      type="button"
      onClick={exportToPDF}
      className="button-container ms-0"
      disabled={isExporting}
    >
      <i className="fa-solid fa-file-pdf me-1 icon-container"></i>
      {isExporting ? "" : "PDF"}
    </button>
  );
};
